import type { ThemeTokens, BrandColors } from '@/shared/ui/theme/tokens';
import { FaceIcon, PinIcon, CardIcon } from './methodIcons';

interface MethodRowProps {
  kind: 'facial' | 'pin' | 'nfc';
  title: string;
  subtitle: string;
  color: string;
  isEnrolled: boolean;
  onEnroll: () => void;
  onUpdate?: () => void;
  onDelete?: () => void;
  deleteDisabled?: boolean;
  deleteDisabledTooltip?: string;
  icon?: React.ReactNode;
  enrolledBadge?: string;
  notEnrolledBadge?: string;
  enrollTooltip?: string;
  updateTooltip?: string;
  deleteTooltip?: string;
  T: ThemeTokens;
  C: BrandColors;
}

import type React from 'react';

/**
 * MethodRow — displays a single biometric enrollment method.
 *
 * Visual fidelity: matches `web/Clock Web.html` methodRow() function exactly.
 * - Not enrolled: single 38×38 `+` button (enroll)
 * - Enrolled, no onDelete (PIN): single 38×38 update button
 * - Enrolled, with onDelete: 38×38 update + 38×38 delete button
 * - deleteDisabled: delete button rendered but `disabled` + `title` tooltip
 *
 * Change: nueva-seccion-de-borrado-perfiles-en-administracion-empleados
 */
export function MethodRow({
  kind,
  title,
  subtitle,
  color,
  isEnrolled,
  onEnroll,
  onUpdate,
  onDelete,
  deleteDisabled = false,
  deleteDisabledTooltip,
  icon,
  enrolledBadge = 'ENROLADO',
  notEnrolledBadge = 'NO ENROLADO',
  enrollTooltip = 'Enrolar',
  updateTooltip = 'Actualizar',
  deleteTooltip = 'Eliminar',
  T,
  C,
}: MethodRowProps) {
  // Default icons per kind if none provided
  const defaultIcon = icon ?? getDefaultIcon(kind);

  return (
    <div
      data-testid={`method-row-${kind}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 16px',
        borderRadius: 13,
        background: T.surfaceBg,
        border: `1px solid ${isEnrolled ? color + '33' : T.border}`,
        boxShadow: isEnrolled ? `0 2px 12px ${color}1f` : 'none',
        marginBottom: 9,
      }}
    >
      {/* Icon with enrollment status badge */}
      <div
        style={{
          position: 'relative',
          width: 54,
          height: 54,
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color,
          opacity: isEnrolled ? 1 : 0.85,
        }}
      >
        {defaultIcon}
        <span
          style={{
            position: 'absolute',
            right: -3,
            bottom: -3,
            width: 20,
            height: 20,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: isEnrolled ? C.success : T.textMuted,
            border: `2px solid ${T.surfaceBg}`,
          }}
        >
          {isEnrolled ? (
            <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : (
            <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          )}
        </span>
      </div>

      {/* Text block */}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: T.clockText }}>{title}</div>
        <div style={{ fontSize: 10, color: T.textMuted, marginTop: 2 }}>{subtitle}</div>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            marginTop: 5,
            padding: '3px 8px',
            borderRadius: 999,
            background: isEnrolled ? `${C.success}18` : `${T.textMuted}1a`,
            color: isEnrolled ? C.success : T.textMuted,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '.04em',
          }}
        >
          <span
            style={{
              width: 5,
              height: 5,
              borderRadius: '50%',
              background: isEnrolled ? C.success : T.textMuted,
            }}
          />
          {isEnrolled ? enrolledBadge : notEnrolledBadge}
        </div>
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', gap: 7, flexShrink: 0 }}>
        {!isEnrolled ? (
          /* Single enroll (+) button */
          <button
            data-testid={`method-row-${kind}-enroll`}
            onClick={onEnroll}
            title={enrollTooltip}
            style={{
              width: 38,
              height: 38,
              background: `${C.blue}14`,
              border: `1px solid ${C.blue}40`,
              borderRadius: 9,
              cursor: 'pointer',
              color: C.blue,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              transition: 'background .15s',
            }}
          >
            <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
              <line x1="12" y1="5" x2="12" y2="19" /><line x1="5" y1="12" x2="19" y2="12" />
            </svg>
          </button>
        ) : (
          <>
            {/* Update button */}
            <button
              data-testid={`method-row-${kind}-update`}
              onClick={onUpdate}
              title={updateTooltip}
              style={{
                width: 38,
                height: 38,
                background: `${C.blue}14`,
                border: `1px solid ${C.blue}40`,
                borderRadius: 9,
                cursor: 'pointer',
                color: C.blue,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background .15s',
              }}
            >
              <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                <path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" />
              </svg>
            </button>

            {/* Delete button — only rendered when onDelete is provided */}
            {onDelete !== undefined && (
              <button
                data-testid={`method-row-${kind}-delete`}
                onClick={deleteDisabled ? undefined : onDelete}
                disabled={deleteDisabled}
                title={deleteDisabled ? deleteDisabledTooltip : deleteTooltip}
                style={{
                  width: 38,
                  height: 38,
                  background: `${C.danger}12`,
                  border: `1px solid ${C.danger}33`,
                  borderRadius: 9,
                  cursor: deleteDisabled ? 'not-allowed' : 'pointer',
                  color: C.danger,
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'background .15s',
                  opacity: deleteDisabled ? 0.5 : 1,
                }}
              >
                <svg width={15} height={15} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                </svg>
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Default icons per method kind ─────────────────────────────────────────────
// Delegates to ./methodIcons (single source of truth shared with MethodChips).

function getDefaultIcon(kind: 'facial' | 'pin' | 'nfc'): React.ReactNode {
  if (kind === 'facial') return <FaceIcon size={38} />;
  if (kind === 'pin')    return <PinIcon  size={38} />;
  return <CardIcon size={38} />;
}
